package com.eladfinish.bible.contest.screens.mikud

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.font.FontWeight
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.analytics.crashlytics.AppCrashlytics
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.choose_name_for_mikud
import com.eladfinish.bible.contest.common.gender.getHebrewGender
import com.eladfinish.bible.contest.common.gender.personalizeGender
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.data.common.FileStorage
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.extensions.padZero
import com.eladfinish.bible.contest.mikud_saved
import com.eladfinish.bible.contest.preset_mikud
import com.eladfinish.bible.contest.screens.mikud.model.BookClickData
import com.eladfinish.bible.contest.screens.mikud.model.BookTargetType
import com.eladfinish.bible.contest.screens.mikud.model.ChapterClickData
import com.eladfinish.bible.contest.screens.mikud.model.MikudData
import com.eladfinish.bible.contest.screens.mikud.model.MikudLevelInfo
import com.eladfinish.bible.contest.screens.mikud.model.MikudPayload
import com.eladfinish.bible.contest.screens.mikud.model.MikudType
import com.eladfinish.bible.contest.screens.mikud.model.RemoteMikud
import com.eladfinish.bible.contest.screens.mikud.model.RemoteMikudMap
import com.eladfinish.bible.contest.screens.mikud.model.RemoteMikudMenuItem
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudAction
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudEffect
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudState
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudState.DialogState
import com.eladfinish.bible.contest.screens.mikud.usecases.BuildMikudLevelsAnnotatedText
import com.eladfinish.bible.contest.screens.mikud.usecases.BuildMikudSummaryUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.GetMikudFromServerUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.GetMikudLevelInfoUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.GetRemoteMikudLevelInfoUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.ParseMikudFromFileUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.ParseMikudFromServerUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.ParseMikudUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.TAG_MIKUD
import com.eladfinish.bible.contest.ui.menus.actions.MikudToolbarAction
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.desc.desc
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Instant

// TODO: לעדכן מיקודים של נוער ומבוגרים
class MikudStore(
    private val settings: Settings,
    private val fileStorage: FileStorage,
    private val mikudRepository: MikudRepository,
    private val parseMikudUseCase: ParseMikudUseCase,
    private val parseMikudFromServerUseCase: ParseMikudFromServerUseCase,
    private val parseMikudFromFileUseCase: ParseMikudFromFileUseCase,
    private val getMikudFromServerUseCase: GetMikudFromServerUseCase,
    private val buildMikudSummaryUseCase: BuildMikudSummaryUseCase,
    private val getMikudLevelInfoUseCase: GetMikudLevelInfoUseCase,
    private val getRemoteMikudLevelInfoUseCase: GetRemoteMikudLevelInfoUseCase,
    private val syllabusCache: SyllabusCache,
    private val buildMikudLevelsAnnotatedText: BuildMikudLevelsAnnotatedText,
    private val debugInfo: DebugInfo,
) : ViewModelStore<MikudState, MikudAction, MikudEffect>(
    tag = "MikudStore",
) {

    companion object {
        const val SKIP_READ_ME_PREF = "skipReadMeMikud"
        const val MIKUD_FOLDER = "mikud"
        private const val MATERIAL_SECTION_PREFIX = "חומרי הלימוד לחידון לתלמידי בתי הספר "
    }

    private val isSkipReadMeChecked: Boolean
        get() = settings.getBoolean(key = SKIP_READ_ME_PREF, defaultValue = false)

    private val isPremium: Boolean
        get() = true // settings.getBoolean(key = Constants.IS_PREMIUM, defaultValue = false)

    private val defaultState: MikudState
        get() = MikudState(
            isSkipReadMeChecked = isSkipReadMeChecked,
            isLoading = false,
            isPremium = isPremium,
            mikudState = mikudRepository.getBookState().toImmutableList(),
            selectedBookIndex = null,
            hebrewGender = settings.getHebrewGender(),
            dialogState = if (isSkipReadMeChecked) {
                DialogState.None
            } else {
                DialogState.Help(isDontShowAgainChecked = isSkipReadMeChecked)
            },
            isDebugMode = debugInfo.isDebugMode,
        )

    @ExperimentalCoroutinesApi
    override val stateFlow = MutableStateFlow(defaultState)

    private var checkboxesWereTouched = false

    override fun handleAction(action: MikudAction): MikudState =
        when (action) {
            is MikudAction.ToolbarClicked -> handleToolbarClicked(toolbarAction = action.toolbarAction)
            is MikudAction.ToggleHelp -> handleToggleHelp(checked = action.shouldShowNextTime)
            is MikudAction.BookToggled -> handleBookToggled(action.bookClickData)
            is MikudAction.ChapterToggled -> handleChapterToggled(action.chapterClickData)
            is MikudAction.PresetSelected -> handlePresetSelected(type = action.type)
            is MikudAction.SaveMikudWithName -> handleSaveMikudWithName(name = action.name)
            is MikudAction.RenameMikud -> handleRenameMikud(oldName = action.oldName, newName = action.newName)
            is MikudAction.LoadMikudByIndex -> handleLoadMikudByIndex(index = action.index)
            is MikudAction.LoadMikudByName -> handleLoadMikudByName(name = action.name)
            is MikudAction.DeleteSavedMikud -> handleDeleteSavedMikud(name = action.name)
            is MikudAction.RequestDeleteSavedMikud -> handleRequestDeleteSavedMikud(name = action.name)
            is MikudAction.ConfirmDeleteSavedMikud -> handleConfirmDeleteSavedMikud(name = action.name)
            is MikudAction.MikudLevelClicked -> handleMikudLevelClicked(payload = action.payload)
            is MikudAction.RenameSavedMikud -> handleRenameSavedMikud(oldName = action.oldName)
            is MikudAction.MikudLoadedFromServer -> handleMikudLoadedFromServer(response = action.response)
            is MikudAction.RemoteMikudClicked -> handleRemoteMikudClicked(itemIndex = action.itemIndex)
            is MikudAction.SyllabusLoaded -> handleSyllabusLoaded(mikudType = action.mikudType, info = action.info)
            is MikudAction.RefreshMikudLevels -> handleRefreshMikudLevels(mikudType = action.mikudType)
            MikudAction.LoadMikudClicked -> handleLoadMikudClicked()
            MikudAction.SaveMikudClicked -> handleSaveMikudClicked()
            MikudAction.CancelDeleteSavedMikud -> handleCancelDeleteSavedMikud()
            MikudAction.PurchasePremiumClicked -> handlePurchasePremiumClicked()
            MikudAction.HideDialog -> handleHideDialog()
            MikudAction.BackClicked -> handleBackClicked()
        }

    private fun handleToolbarClicked(toolbarAction: MikudToolbarAction): MikudState {
        return when (toolbarAction) {
            MikudToolbarAction.SelectAllMaterial -> handleSelectAllOrNone(bool = true)
            MikudToolbarAction.DeselectAllMaterial -> handleSelectAllOrNone(bool = false)
            MikudToolbarAction.Help -> handleHelpClicked()
            MikudToolbarAction.ShowMikud -> handleMikudSummaryClicked()
        }
    }

    private fun handleBackClicked(): MikudState {
        if (!isPremium && checkboxesWereTouched) {
            return currentState.copy(
                dialogState = DialogState.PremiumRequired(
                    message = "על מנת לשמור את המיקוד שנבחר יש לשדרג לגרסה מלאה."
                )
            )
        }
        saveSelectedMikudAndExit()
        return currentState
    }

    private fun saveSelectedMikudAndExit() {
        val numOfChaptersInMikud = currentState.mikudState.sumOf { it.chapters.count { bool -> bool } }

        settings.putInt(Constants.PREF_NUM_OF_CHAPTERS_IN_MIKUD, numOfChaptersInMikud)
        settings.putString(Constants.PREF_BIBLE_BOOKS_IN_MIKUD, booleanListOfBooks().toString())
        mikudRepository.saveMikud(currentState.mikudState)

        launchEffect {
            sideEffectFlow.emit(MikudEffect.ShowToastRes(SharedRes.strings.mikud_saved))
            delay(duration = 200.milliseconds)
            sideEffectFlow.emit(MikudEffect.NavigateBack)
        }
    }

    private fun handleLoadMikudClicked(): MikudState {
        val presets = mikudRepository.mikudimTypes
        val savedFiles = fileStorage.listFiles(MIKUD_FOLDER)
        return currentState.copy(
            dialogState = DialogState.LoadMikudMenu(
                title = SharedRes.strings.preset_mikud.desc()
                    .personalizeGender(settings.getHebrewGender()),
                presets = presets.toImmutableList(),
                savedItems = savedFiles.toImmutableList(),
            )
        )
    }

    private fun handleLoadMikudByIndex(index: Int): MikudState {
        val presets = mikudRepository.mikudimTypes
        if (index < presets.size) {
            // בחירה במיקוד מובנה
            return handlePresetSelected(type = presets[index].mikudType)
        } else {
            // בחירה במיקוד שמור (האינדקס מותאם)
            val savedFiles = fileStorage.listFiles(MIKUD_FOLDER)
            val savedIndex = index - presets.size
            if (savedIndex < savedFiles.size) {
                return handleLoadMikudByName(name = savedFiles[savedIndex])
            }
        }
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleSaveMikudClicked(): MikudState {
        return currentState.copy(
            dialogState = DialogState.MikudNameEntry(
                title = SharedRes.strings.choose_name_for_mikud.desc()
                    .personalizeGender(settings.getHebrewGender())
            )
        )
    }

    private fun handleSaveMikudWithName(name: String): MikudState {
        val content = getMikudToFile()
        val success = fileStorage.saveFile(MIKUD_FOLDER, name, content)
        launchEffect {
            if (success) {
                sideEffectFlow.emit(MikudEffect.ShowToast("מיקוד '$name' נשמר בהצלחה"))
            } else {
                sideEffectFlow.emit(MikudEffect.ShowToast("שגיאה בשמירת המיקוד"))
            }
        }
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleRenameMikud(oldName: String, newName: String): MikudState {
        val success = fileStorage.renameFile(MIKUD_FOLDER, oldName, newName)
        launchEffect {
            if (success) {
                sideEffectFlow.emit(MikudEffect.ShowToast("שם המיקוד שונה בהצלחה ל־'$newName'"))
            } else {
                sideEffectFlow.emit(MikudEffect.ShowToast("שגיאה בשינוי שם המיקוד"))
            }
        }
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleLoadMikudByName(name: String): MikudState {
        val data = fileStorage.readFile(MIKUD_FOLDER, name)
        if (data != null) {
            val parsedMikud = parseMikudFromFileUseCase(data)
            updateMikud(parsedMikud)
            checkboxesWereTouched = true
            launchEffect {
                sideEffectFlow.emit(MikudEffect.ShowToast("מיקוד '$name' נטען"))
            }
        }
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleDeleteSavedMikud(name: String): MikudState {
        fileStorage.deleteFile(MIKUD_FOLDER, name)
        return handleLoadMikudClicked()
    }

    private fun handleRequestDeleteSavedMikud(name: String): MikudState {
        return currentState.copy(dialogState = DialogState.ConfirmDeleteSavedMikud(name))
    }

    private fun handleConfirmDeleteSavedMikud(name: String): MikudState {
        val success = fileStorage.deleteFile(MIKUD_FOLDER, name)

        launchEffect {
            if (success) {
                sideEffectFlow.emit(MikudEffect.ShowToast("מיקוד '$name' נמחק"))
            } else {
                sideEffectFlow.emit(MikudEffect.ShowToast("שגיאה במחיקת המיקוד '$name'"))
            }
        }

        return if (success) {
            // Refresh the list after deletion
            handleLoadMikudClicked().copy(dialogState = DialogState.None)
        } else {
            // Stay in the confirmation dialog so the user can retry or cancel
            currentState.copy(dialogState = DialogState.ConfirmDeleteSavedMikud(name))
        }
    }

    private fun handleCancelDeleteSavedMikud(): MikudState {
        // Go back to the menu (instead of closing everything)
        return handleLoadMikudClicked()
    }

    private fun handleRenameSavedMikud(oldName: String): MikudState {
        return currentState.copy(
            dialogState = DialogState.MikudRenameEntry(
                title = SharedRes.strings.choose_name_for_mikud.desc()
                    .personalizeGender(hebrewGender = settings.getHebrewGender()),
                initialValue = oldName,
            )
        )
    }

    private fun handlePresetSelected(type: MikudType): MikudState {
        return when (type) {
            MikudType.YOUTH, MikudType.ADULTS -> {
                launchEffect {
                    val info = loadSyllabusInfo(type = type, forceRefresh = false)
                    dispatch(MikudAction.SyllabusLoaded(mikudType = type, info = info))
                }
                currentState.copy(isLoading = true, dialogState = DialogState.None)
            }

            MikudType.FULL -> handleSelectAllOrNone(true).copy(dialogState = DialogState.None)

            MikudType.SERVER -> {
                loadMikudFromServer()
                currentState.copy(
                    isLoading = true,
                    dialogState = DialogState.None,
                )
            }
        }
    }

    /**
     * Fetches the youth/adults syllabus from the server (content/syllabuses on GitHub), unless
     * [forceRefresh] is false and a cached copy is still within its one-month TTL. Falls back to
     * the bundled local asset if the remote fetch fails for any reason (no internet, a server
     * error, or an empty/unparseable response) -- the local asset always succeeds.
     */
    private suspend fun loadSyllabusInfo(type: MikudType, forceRefresh: Boolean): MikudLevelInfo {
        if (!forceRefresh) {
            syllabusCache.get(type)?.let { return it }
        }
        val remote = getRemoteMikudLevelInfoUseCase(type)
        if (remote != null) {
            syllabusCache.put(type, remote)
            return remote
        }
        return getMikudLevelInfoUseCase(type)
    }

    private fun handleSyllabusLoaded(mikudType: MikudType, info: MikudLevelInfo): MikudState =
        currentState.copy(isLoading = false, dialogState = buildMikudLevelsDialogState(mikudType, info))

    private fun handleRefreshMikudLevels(mikudType: MikudType): MikudState {
        launchEffect {
            val info = loadSyllabusInfo(type = mikudType, forceRefresh = true)
            dispatch(MikudAction.SyllabusLoaded(mikudType = mikudType, info = info))
        }
        return currentState.copy(isLoading = true, dialogState = DialogState.None)
    }

    private fun buildMikudLevelsDialogState(mikudType: MikudType, info: MikudLevelInfo): DialogState.MikudLevels {
        val (clickableTitle, underlinedTitle, title) = when (mikudType) {
            MikudType.YOUTH -> Triple("בתי הספר", "לתלמידי בתי הספר", "חידון התנ\"ך לנוער")
            MikudType.ADULTS -> Triple("חידון", "חומרי הלימוד", "חידון התנ\"ך למבוגרים")
            else -> error("buildMikudLevelsDialogState called with unsupported type: $mikudType")
        }
        val annotated = buildMikudLevelsAnnotatedText(
            info = info,
            clickableTitle = clickableTitle,
            underlinedTitle = underlinedTitle,
        )
        return DialogState.MikudLevels(
            mikudType = mikudType,
            info = info,
            title = title,
            annotatedText = annotated,
        )
    }

    private fun parseMikudPayload(payload: String): MikudPayload? {
        val parts = payload.split("|")
        if (parts.size < 2) return null

        val start = parts[0].toIntOrNull() ?: return null
        val end = parts[1].toIntOrNull() ?: return null
        val schoolType = parts.getOrNull(2).orEmpty()

        return MikudPayload(
            lineStart = start,
            lineEnd = end,
            schoolType = schoolType,
        )
    }

    private fun findSchoolSectionRange(full: String, schoolType: String): IntRange? {
        val header = MATERIAL_SECTION_PREFIX + schoolType
        val sectionStart = full.indexOf(header)
        if (sectionStart == -1) return null

        // Find the next "חומרי הלימוד..." header after this one (start of next section)
        val nextSectionStart = full.indexOf(MATERIAL_SECTION_PREFIX, startIndex = sectionStart + header.length)
        val sectionEndExclusive = if (nextSectionStart == -1) full.length else nextSectionStart

        return sectionStart until sectionEndExclusive
    }

    private fun handleMikudLevelClicked(payload: String): MikudState {
        val parsed = parseMikudPayload(payload) ?: return currentState

        val dialog = currentState.dialogState as? DialogState.MikudLevels ?: return currentState
        val annotated = dialog.annotatedText
        val full = annotated.text

        if (parsed.lineStart !in 0..full.length) return currentState
        if (parsed.lineEnd !in 0..full.length) return currentState
        if (parsed.lineStart >= parsed.lineEnd) return currentState

        val sectionRange = findSchoolSectionRange(full, parsed.schoolType) ?: (0 until full.length)

        val levelsInSection = annotated
            .getStringAnnotations(tag = TAG_MIKUD, start = sectionRange.first, end = sectionRange.last + 1)
            .mapNotNull { ann ->
                val p = parseMikudPayload(ann.item) ?: return@mapNotNull null
                if (p.schoolType == parsed.schoolType) p else null
            }
            .distinctBy { it.lineStart }
            .sortedBy { it.lineStart }

        if (levelsInSection.isEmpty()) return currentState

        val clickedIndex = levelsInSection.indexOfFirst { it.lineStart == parsed.lineStart }
        if (clickedIndex == -1) return currentState

        val chunkStart = levelsInSection.first().lineStart
        val chunkEndExclusive =
            levelsInSection.getOrNull(clickedIndex + 1)?.lineStart ?: (sectionRange.last + 1)

        if (chunkStart !in 0..full.length) return currentState
        if (chunkEndExclusive !in 0..full.length) return currentState
        if (chunkStart >= chunkEndExclusive) return currentState

        val chunk = full.substring(chunkStart, chunkEndExclusive).trimEnd()

        val parsedMikud = try {
            parseMikudUseCase(text = chunk)
        } catch (e: Exception) {
            AppCrashlytics.logException(e)
            launchEffect { sideEffectFlow.emit(MikudEffect.ShowToast("שגיאה בבחירת המיקוד")) }
            return currentState
        }

        checkboxesWereTouched = true

        val newMikudState = parsedMikud
            .map { it.copy() }
            .toImmutableList()

        val newAnnotated = AnnotatedString.Builder(dialog.annotatedText)
            .apply {
                addStyle(
                    style = SpanStyle(fontWeight = FontWeight.Bold),
                    start = chunkStart,
                    end = chunkEndExclusive,
                )
            }
            .toAnnotatedString()

        val levelLabel = buildLevelLabel(full = full, start = parsed.lineStart, end = parsed.lineEnd)

        val updatedState = currentState.copy(
            mikudState = newMikudState,
            dialogState = dialog.copy(annotatedText = newAnnotated),
        )

        launchEffect {
            if (levelLabel.isNotBlank()) {
                sideEffectFlow.emit(
                    MikudEffect.ShowToast(
                        "נבחר מיקוד: $levelLabel${if (parsed.schoolType.isNotBlank()) " (${parsed.schoolType})" else ""}"
                    )
                )
            }
            delay(duration = 500.milliseconds)
            dispatch(MikudAction.HideDialog)
        }

        return updatedState
    }

    private fun buildLevelLabel(full: String, start: Int, end: Int): String {
        // Extract the clicked line ("• ...") for a user-friendly label.
        val lineEnd = full.indexOf('\n', start).let { idx ->
            when {
                idx == -1 -> end
                idx < end -> idx
                else -> end
            }
        }

        val line = full.substring(start, lineEnd).trim()
        if (line.isBlank()) return ""

        // Mimic legacy: "החומר ל" + text after the first space, until " –" (if present).
        val endOfLevelName = line.indexOf(" –").let { if (it == -1) line.length else it }
        val firstSpace = line.indexOf(' ')
        if (firstSpace == -1 || firstSpace + 1 >= endOfLevelName) return line

        return "החומר ל" + line.substring(firstSpace + 1, endOfLevelName).trim()
    }

    private fun loadMikudFromServer() {
        try {
            launchEffect {
                val response = getMikudFromServerUseCase()
                dispatch(MikudAction.MikudLoadedFromServer(response = response))
            }
        } catch (_: Exception) {
            launchEffect {
                sideEffectFlow.emit(MikudEffect.ShowToast("שגיאה בהורדת המיקודים"))
            }
        } finally {
            updateState { it.copy(isLoading = false) }
        }
    }

    private fun handleMikudLoadedFromServer(response: String): MikudState {
        return try {
            val remoteMikudList: List<RemoteMikud> =
                response
                    .convertToDataClass<RemoteMikudMap>()
                    .values
                    .sortedByDescending { it.createdAt }

            Napier.d("handleMikudLoadedFromServer: items=${remoteMikudList.size}")

            currentState.copy(
                isLoading = false,
                dialogState = DialogState.RemoteMikudMenu(
                    title = "חידונים נוספים",
                    items = remoteMikudList.map { it.toUiItem() }.toImmutableList(),
                )
            )
        } catch (e: Exception) {
            AppCrashlytics.logException(e)
            launchEffect {
                sideEffectFlow.emit(MikudEffect.ShowToast("שגיאה בעיבוד נתוני השרת"))
            }
            currentState.copy(isLoading = false)
        }
    }

    private fun handleRemoteMikudClicked(itemIndex: Int): MikudState {
        val dialog = currentState.dialogState as? DialogState.RemoteMikudMenu ?: return currentState
        val item = dialog.items.getOrNull(itemIndex) ?: return currentState

        return try {
            val parsedMikud = parseMikudFromServerUseCase(remoteMikudContent = item.mikudContent)

            updateMikud(parsedMikud)
            checkboxesWereTouched = true

            launchEffect {
                sideEffectFlow.emit(MikudEffect.ShowToast("מיקוד '${item.name}' נטען מהשרת"))
            }

            currentState.copy(dialogState = DialogState.None)
        } catch (e: Exception) {
            AppCrashlytics.logException(e)
            launchEffect {
                sideEffectFlow.emit(MikudEffect.ShowToast("שגיאה בטעינת המיקוד מהשרת"))
            }
            currentState
        }
    }

    private fun handleHelpClicked(): MikudState {
        return currentState.copy(
            dialogState = DialogState.Help(isDontShowAgainChecked = currentState.isSkipReadMeChecked),
        )
    }

    private fun handleToggleHelp(checked: Boolean): MikudState {
        settings.putBoolean(key = SKIP_READ_ME_PREF, value = checked)
        return currentState.copy(isSkipReadMeChecked = checked)
    }

    private fun handleMikudSummaryClicked(): MikudState {
        val summary = buildMikudSummaryUseCase(currentState.mikudState)
        return currentState.copy(
            dialogState = DialogState.MikudSummary(summary = summary),
        )
    }

    private fun handlePurchasePremiumClicked(): MikudState {
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleHideDialog(): MikudState {
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleSelectAllOrNone(bool: Boolean): MikudState {
        checkboxesWereTouched = true

        val newMikudState = currentState.mikudState
            .map { book ->
                book.copy(chapters = List(book.chapters.size) { bool }.toImmutableList())
            }
            .toImmutableList()

        return currentState.copy(
            mikudState = newMikudState,
            selectedBookIndex = null,
        )
    }

    private fun handleBookToggled(bookClickData: BookClickData): MikudState {
        checkboxesWereTouched = true
        val (bookTargetType, index, selected) = bookClickData
        return if (bookTargetType == BookTargetType.CHECKBOX) {
            val newMikudState = currentState.mikudState
                .mapIndexed { i, book ->
                    if (i != index) {
                        book
                    } else {
                        book.copy(chapters = List(book.chapters.size) { selected }.toImmutableList())
                    }
                }
                .toImmutableList()

            currentState.copy(
                mikudState = newMikudState,
                selectedBookIndex = index,
            )
        } else {
            currentState.copy(
                selectedBookIndex = index,
            )
        }
    }

    private fun handleChapterToggled(chapterClickData: ChapterClickData): MikudState {
        val (bookIndex, chapterIndex, selected) = chapterClickData
        checkboxesWereTouched = true

        val newMikudState = currentState.mikudState
            .mapIndexed { i, book ->
                if (i != bookIndex) return@mapIndexed book

                val newChapters = book.chapters
                    .toMutableList()
                    .apply { set(chapterIndex, selected) }
                    .toImmutableList()

                book.copy(chapters = newChapters)
            }
            .toImmutableList()

        return currentState.copy(
            mikudState = newMikudState,
        )
    }

    private fun updateMikud(parsedMikud: MikudData) {
        Napier.d("parsedMikud_ $parsedMikud")

        val newMikudState = parsedMikud
            .map { it.copy() }
            .toImmutableList()

        updateState {
            it.copy(
                isLoading = false,
                mikudState = newMikudState,
            )
        }
    }

    private fun booleanListOfBooks(): List<Boolean> =
        currentState.mikudState.map { it.isNotEmpty() }

    private fun getMikudToFile(): String =
        currentState.mikudState
            .filter { it.isNotEmpty() }
            .joinToString(separator = ",\n") { book ->
                "\"${book.english}\" : " +
                        book.chapters
                            .mapIndexedNotNull { index, included ->
                                if (included) index + 1 else null
                            }
            }
            .let { "{\n$it\n}" }
            .replace(oldValue = " ", newValue = "")

    private fun RemoteMikud.toUiItem(): RemoteMikudMenuItem =
        RemoteMikudMenuItem(
            name = name,
            info = info,
            createdAtMillis = createdAt,
            createdAtText = formatDateDdMmYyyy(epochMillis = createdAt),
            mikudContent = mikudContent,
        )

    private fun formatDateDdMmYyyy(epochMillis: Long): String {
        val dt = Instant.fromEpochMilliseconds(epochMilliseconds = epochMillis)
            .toLocalDateTime(TimeZone.currentSystemDefault())
        val dd = dt.day.padZero()
        val mm = dt.month.number.padZero()
        val yyyy = dt.year.toString()
        return "$dd.$mm.$yyyy"
    }
}
